import {
  BARRA_ECCEZIONI,
  VOCE_DEL_PREFISSO,
  normalizzaPercorso,
  staSotto,
  type EccezioneBarra,
  type VoceBarra,
} from '@/components/layout/tabBarRoutes';
import { elencoMinigiochi } from '@/lib/minigiochi';

/**
 * LE REGOLE DELLA BARRA NELLA V3 (fase 2, PR 5 e 6): quale voce si accende, quali schermate sono senza barra, e dove serve l'uscita. Stanno QUI, sotto `src/v3/`, e non in
 * `tabBarRoutes.ts` (riserva di flotta su #766, R-b): lì finivano nel bundle di produzione come codice morto, con i loro testi. `AppShell` li prende da `lib/v3.ts`
 * (`showsTabBarV3`, `serveUscitaV3`: `undefined` in una build senza v3, e l'import statico qui sotto lo piega la `const` sul flag, come la palette); la barra della v3 e i test
 * li importano direttamente. Le regole di OGGI (`voceAccesa`, `BARRA_ECCEZIONI`, `showsTabBar`) restano in `tabBarRoutes.ts` e non si toccano.
 */

/**
 * LE VOCI DELLA BARRA A QUATTRO VOCI della v3 (ADR-0242 §1, fase 2): News · Amici · Home · Impostazioni. Non c'e' piu' il Profilo. «Home» e' il nome che la v3
 * da' alla voce che oggi si chiama Hub: stessa rotta `/`, stessa chiave `hub`, e si accende con la stessa regola (tutto cio' che non e' delle altre tre).
 */
export type VoceBarraV3 = Exclude<VoceBarra, 'profile'> | 'minigiochi';

/** La pagina dei minigiochi (ADR-0264 §2): la rotta della quinta voce. Le rotte dei GIOCHI non stanno qui: si leggono da `elencoMinigiochi()`. */
export const ROTTA_MINIGIOCHI = '/minigiochi';

/**
 * La regola di `voceAccesa` nella v3: IDENTICA, tranne UNA riga, la sola che ADR-0242 §5 supera dello 0196 §3: «`/profile` esatto → Profilo» non puo' valere perche'
 * la voce non c'e', e su `/profile` si accende Impostazioni. `/profile/:id` (un altro giocatore) resta Amici. ⚠️ L'ordine conta come sopra: l'esatto prima del prefisso.
 * Derivata dalla tabella di oggi e non riscritta: due elenchi di prefissi divergerebbero al primo ritocco.
 */
const VOCE_DEL_PREFISSO_V3: ReadonlyArray<readonly [prefisso: string, voce: VoceBarraV3, soloEsatto?: true]> = /* @__PURE__ */ VOCE_DEL_PREFISSO.map(
  ([prefisso, voce, soloEsatto]) => [prefisso, voce === 'profile' ? 'settings' : voce, soloEsatto] as const,
);

/**
 * ADR-0264 §2: «Minigiochi» si accende su `/minigiochi` e sulle rotte dei giochi dell'elenco, anche sotto di loro (oggi `/solitario` e `/2048`). Le rotte si leggono dal campo `rotta` di
 * `elencoMinigiochi()` a ogni chiamata, mai da una seconda lista. Con l'elenco vuoto (build senza minigiochi) resta la sola `/minigiochi`, e la voce nella barra non c'e'. Viene PRIMA della tabella
 * di oggi: `/solitario` e `/2048` li' non hanno una riga, e fin qui cadevano su Home.
 */
function eDelleMinigiochi(percorso: string): boolean {
  return staSotto(percorso, ROTTA_MINIGIOCHI) || elencoMinigiochi().some((m) => staSotto(percorso, normalizzaPercorso(m.rotta)));
}

export function voceAccesaV3(pathname: string): VoceBarraV3 {
  const percorso = normalizzaPercorso(pathname);
  if (eDelleMinigiochi(percorso)) return 'minigiochi';
  const riga = VOCE_DEL_PREFISSO_V3.find(([prefisso, , soloEsatto]) => (soloEsatto ? percorso === prefisso : staSotto(percorso, prefisso)));
  return riga?.[1] ?? 'hub';
}

/**
 * `percorso` e' `pattern` o sta sotto di lui, CONFRONTANDO PER SEGMENTO, con un segmento `:x` che vale «un qualunque segmento non vuoto» (l'id di una rotta
 * del router). Come `staSotto`, tranne per i `:x`: `/chat/:x` copre `/chat/r-1`, non `/chat` (la LISTA tiene la barra) e non `/chatter/r-1`.
 * Entrambi gia' normalizzati (minuscolo, senza slash finali: un id in maiuscolo non cambia niente, il confronto e' sul disegno della rotta).
 */
function combaciaConPattern(percorso: string, pattern: string): boolean {
  const seg = percorso.split('/').filter(Boolean);
  const pat = pattern.split('/').filter(Boolean);
  return pat.every((p, i) => (p.startsWith(':') ? seg[i] !== undefined : p === seg[i]));
}

/**
 * LE ECCEZIONI della v3 (ADR-0242 §2): le schermate senza barra nella v3. Parte da quelle di oggi (la v3 le tiene tutte) e ci aggiunge le sue: il match di torneo
 * (Limulo e Laravel), la conversazione e le stanze di chat, ciascuna con il perche'. `percorso` puo' avere segmenti `:x` (v. `combaciaConPattern`): le LISTE
 * (`/limulo/tournaments/:x/matches`, `/friends/messages`, `/chat`) tengono la barra. ⚠️ Il ramo di oggi (`BARRA_ECCEZIONI`, `showsTabBar`) non si tocca.
 *
 * I TAVOLI VIVI (`/backgammon/match`, `/chess/match`, `/bridge/tavolo`, `/burraco/tavolo`) NON sono qui: la barra c'e' (Luca, I-383, riconfermato da ADR-0242 §5), con la
 * conferma prima di lasciarli. Il confine e' la ROTTA: dal match si va al tavolo con una navigazione. Neppure `/duels/result/:gameTag` e `/chinesepoker/result/:id`.
 *
 * Gli ESITI 25-29 (round vinto, eliminato, 1°, 2°, 3° posto) non sono rotte: sono il `ModaleEsito`, un livello `fixed inset-0 z-[70]` sopra la barra (`z-10`, nel
 * flusso della shell), e la copre senza elenco. ADR-0242 §2 lascia la scelta a chi implementa: un livello che copre = niente rotta e niente voce qui.
 */
// ⚠️ `/* @__PURE__ */ BARRA_ECCEZIONI.concat(…)` e non `[...BARRA_ECCEZIONI, …]`: in produzione il modulo non e' raggiungibile (lo importa solo `lib/v3.ts`, dietro la `const` sul flag), ma uno SPREAD a
// livello di modulo e' un'iterazione che il bundler non sa togliere, e restava in `index-*.js` (riserva 2 di flotta su #772, dopo la `.map` di #768). `verify-v3 --bundle` guarda i percorsi di questo elenco.
export const BARRA_ECCEZIONI_V3: readonly EccezioneBarra[] = /* @__PURE__ */ BARRA_ECCEZIONI.concat([
  {
    percorso: '/limulo/tournaments/:x/matches/:x',
    perche: 'il match di torneo di Limulo (`LimuloMatchDetail`): ADR-0242 §2, le schermate 12a-12f, 13 e 14 del pacchetto. Il tavolo e` un\'altra rotta, e tiene la barra (I-383).',
  },
  {
    percorso: '/matches/:x',
    perche: 'il match di torneo Laravel (`MatchDetail`): ADR-0242 §2. Stessa famiglia di quello di Limulo; la lista `/tournaments/:id` tiene la barra.',
  },
  {
    percorso: '/friends/messages/:x',
    perche: 'la conversazione (`DmConversazione`, schermata 22): ADR-0242 §2. L\'elenco dei messaggi sta dentro `/friends` e tiene la barra.',
  },
  {
    percorso: '/chat/:x',
    perche: 'la stanza (`ChatRoom`, con il campo per scrivere): ADR-0242 §2, scelta di Luca a parte (L-3537 (a)). L\'elenco `/chat` tiene la barra.',
  },
]);

/** L'eccezione della v3 che copre il percorso, o `undefined` se la barra c'e'. */
export function eccezioneBarraV3(pathname: string): EccezioneBarra | undefined {
  const percorso = normalizzaPercorso(pathname);
  return BARRA_ECCEZIONI_V3.find((e) => combaciaConPattern(percorso, normalizzaPercorso(e.percorso)));
}

/**
 * Le eccezioni la cui schermata ha GIA' un suo «indietro» e non vuole l'uscita della shell: il flusso a passi dell'assistente avatar (`AvatarAssistant`, ha il suo header).
 * Le quattro rotte di ADR-0242 §2 non ce l'hanno. Un elenco di percorsi e non un campo sulle eccezioni: l'elenco delle eccezioni di oggi non si tocca e non si copia.
 */
export const CON_USCITA_PROPRIA: readonly string[] = ['/avatars/new/assistant'];

/**
 * La shell deve montare `UscitaV3`: la rotta e' senza barra nella v3 E la sua schermata non ha un «indietro» suo. ADR-0242 «Conseguenze»: nessuna schermata senza barra resta
 * senza uscita. Una rotta con la barra non ne ha bisogno (la barra e' l'uscita), e il flusso a passi di oggi ha il suo header.
 */
export function serveUscitaV3(pathname: string): boolean {
  const e = eccezioneBarraV3(pathname);
  return e !== undefined && !CON_USCITA_PROPRIA.includes(e.percorso);
}

/** La barra c'e' SU OGNI SCHERMATA DELLA SHELL tranne quelle di `BARRA_ECCEZIONI_V3`: la regola di `showsTabBar`, sull'elenco della v3. */
export function showsTabBarV3(pathname: string): boolean {
  return eccezioneBarraV3(pathname) === undefined;
}
